import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { gutters } from 'app/spacing';
import { ButtonGroupImageButton } from 'components/Inputs/ButtonGroup/Button';
import Icon from 'components/Kizen/Icon';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import TypeCreateEntity from './images/type-create-entity.svg?react';
import TypeCreateFields from './images/type-create-fields.svg?react';
import TypeCreateTeamAndActivities from './images/type-create-team-and-activities.svg?react';
import TypeCreateActionBlock from './images/type-create-action-block.svg?react';
import TypeCreateLeadSources from './images/type-create-lead-sources.svg?react';
import TypeCreateTimeline from './images/type-create-timeline.svg?react';
import TypeCreateRelatedPipelineFields from './images/type-create-related-pipeline-fields.svg?react';
import { PluginContext } from 'ts-components/Plugins/context';
import { PluginIcon } from 'ts-components/Plugins/PluginIcon';
import KizenTypography from 'app/kizentypo';

const RECORD_DETAIL_SURFACE = 'records';

const CompoundIcon = styled.div`
  display: flex;
  column-gap: 10px;

  > i {
    pointer-events: none;
  }

  height: 40px;
  margin-bottom: 8px;
`;

const getOptions = ({
  t,
  showAllOptions = false,
  canUseRelatedPipelinesBlock,
}) => {
  const defaultOptions = [
    {
      value: 'related_object_fields',
      label: t('Related Object Fields'),
      image: <TypeCreateEntity />,
    },
  ];

  if (showAllOptions) {
    return [
      {
        value: 'fields',
        label: t('Fields'),
        image: <TypeCreateFields />,
      },
      {
        value: 'team_and_activities',
        label: t('Team and Activities'),
        image: <TypeCreateTeamAndActivities />,
      },
      {
        value: 'action_block',
        label: t('Action Block'),
        image: <TypeCreateActionBlock />,
      },
      {
        value: 'lead_sources',
        label: t('Lead Sources'),
        image: <TypeCreateLeadSources />,
      },
      {
        value: 'timeline',
        label: t('Timeline'),
        image: <TypeCreateTimeline />,
      },
      canUseRelatedPipelinesBlock
        ? {
            value: 'related_pipelines',
            label: t('Related Pipelines'),
            image: <TypeCreateRelatedPipelineFields />,
          }
        : null,
      {
        value: 'related_object_fields',
        label: t('Related Object Fields'),
        image: <TypeCreateEntity />,
      },
      {
        value: 'custom_content',
        label: t('Custom Content (Builder)'),
        image: (
          <CompoundIcon>
            <Icon icon="text" />
            <Icon icon="image-landscape" />
          </CompoundIcon>
        ),
      },
    ].filter(Boolean);
  }

  return defaultOptions;
};

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: ${gutters.spacing(4)}px;
  row-gap: ${gutters.spacing(4)}px;
`;

const PluginButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: ${gutters.spacing(4)}px;
  row-gap: ${gutters.spacing(4)}px;
`;

const IntegrationsSection = styled.div`
  margin-top: ${gutters.spacing(4)}px;
`;

const SectionLabel = styled(KizenTypography)`
  display: block;
  margin-bottom: ${gutters.spacing(3)}px;
`;


export default function GroupedButtonGrid({
  onChange,
  value,
  allowNoChildStep,
  showAllOptions,
  canUseRelatedPipelinesBlock,
}) {
  const { t } = useTranslation();
  const preReleaseFeatures = usePreReleaseFeatures();
  const { businessPluginAppsByApiName, pluginBlocks } =
    useContext(PluginContext);

  const options = useMemo(
    () =>
      getOptions({
        preReleaseFeatures,
        allowNoChildStep,
        t,
        showAllOptions,
        canUseRelatedPipelinesBlock,
      }),
    [
      preReleaseFeatures,
      allowNoChildStep,
      t,
      showAllOptions,
      canUseRelatedPipelinesBlock,
    ]
  );

  const activePluginEntries = useMemo(() => {
    if (!showAllOptions) return [];
    return Object.entries(businessPluginAppsByApiName)
      .filter(([, bizPlugin]) => !bizPlugin.disabled)
      .map(([apiName, bizPlugin]) => {
        const blockEntry = pluginBlocks.find(
          (p) => p.plugin_api_name === apiName
        );
        const blocks = (blockEntry?.blocks ?? []).filter(
          (block) =>
            !block.types?.length || block.types.includes(RECORD_DETAIL_SURFACE)
        );
        return {
          plugin_api_name: apiName,
          plugin_name: bizPlugin.plugin_app.name,
          thumbnail: bizPlugin.plugin_app.thumbnail,
          blocks,
        };
      })
      .filter((entry) => entry.blocks.length > 0);
  }, [businessPluginAppsByApiName, pluginBlocks, showAllOptions]);

  return (
    <>
      {options?.length ? (
        <ButtonGrid>
          {options.map((option) => (
            <ButtonGroupImageButton
              key={option.value}
              selected={option.value === value}
              onClick={() => onChange(option)}
              option={option}
              data-qa={option.value}
            />
          ))}
        </ButtonGrid>
      ) : (
        ''
      )}

      {activePluginEntries.length > 0 && (
        <IntegrationsSection>
          <SectionLabel as="h3" type="subheader">
            {t('Integrations')}
          </SectionLabel>
          <PluginButtonGrid>
            {activePluginEntries.map((entry) => (
              <ButtonGroupImageButton
                key={entry.plugin_api_name}
                selected={value === `plugin_block:${entry.plugin_api_name}`}
                option={{
                  label: entry.plugin_name,
                  image: <PluginIcon thumbnail={entry.thumbnail} />,
                }}
                data-qa={`plugin-block-${entry.plugin_api_name}`}
                onClick={() =>
                  onChange({
                    value: 'plugin_block',
                    pluginApiName: entry.plugin_api_name,
                  })
                }
              />
            ))}
          </PluginButtonGrid>
        </IntegrationsSection>
      )}
    </>
  );
}
